import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Gift, Briefcase, Heart, DollarSign, Trophy, Loader2, Plus, Award, Download, Upload, Lock, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BenefitsPage = () => {
  const { user } = useAuth();
  const isHR = user?.role === 'admin' || user?.role === 'hr';
  const [benefits, setBenefits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rewards, setRewards] = useState([]);
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const [rewardYear, setRewardYear] = useState(String(new Date().getFullYear()));
  const [rewardYears, setRewardYears] = useState([]);
  const [grantOpen, setGrantOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [grantForm, setGrantForm] = useState({ employee_id: '', bond_type: 'appreciation', tenure_months: '6', description: '' });
  const [granting, setGranting] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const certRef = useRef(null);
  const [uploadTargetId, setUploadTargetId] = useState(null);

  // Employee Appreciation Bond catalogue (5 fixed types, locked amounts)
  const BOND_CATALOGUE = [
    { key: 'pinnacle',     label: 'Pinnacle Bond',     amount: 5000 },
    { key: 'excellence',   label: 'Excellence Bond',   amount: 4000 },
    { key: 'impact',       label: 'Impact Bond',       amount: 3000 },
    { key: 'commitment',   label: 'Commitment Bond',   amount: 2000 },
    { key: 'appreciation', label: 'Appreciation Bond', amount: 1000 },
  ];
  const selectedBond = BOND_CATALOGUE.find(b => b.key === grantForm.bond_type) || BOND_CATALOGUE[BOND_CATALOGUE.length - 1];

  useEffect(() => { fetchBenefits(); }, []);

  const fetchMyRewards = useCallback(async () => {
    setRewardsLoading(true);
    try {
      const url = isHR ? `${API_URL}/api/rewards` : `${API_URL}/api/rewards/my`;
      const params = { year: rewardYear || undefined };
      const [rres, yres] = await Promise.all([
        axios.get(url, { params, withCredentials: true }),
        axios.get(`${API_URL}/api/rewards/years`, { withCredentials: true }),
      ]);
      setRewards(rres.data?.rewards || []);
      setRewardYears(yres.data?.years || []);
    } catch (err) {
      // silent when nothing exists
    } finally { setRewardsLoading(false); }
  }, [isHR, rewardYear]);

  useEffect(() => { fetchMyRewards(); }, [fetchMyRewards]);

  const openGrant = async () => {
    if (employees.length === 0) {
      try {
        const res = await axios.get(`${API_URL}/api/employees`, { withCredentials: true });
        setEmployees(res.data || []);
      } catch { /* silent */ }
    }
    setGrantForm({ employee_id: '', bond_type: 'appreciation', tenure_months: '6', description: '' });
    setGrantOpen(true);
  };

  const submitGrant = async () => {
    if (!grantForm.employee_id || !grantForm.bond_type || !grantForm.description) {
      toast.error('Employee, bond type and description are required'); return;
    }
    setGranting(true);
    try {
      const emp = employees.find(e => (e.user_id || e.id) === grantForm.employee_id);
      const bond = BOND_CATALOGUE.find(b => b.key === grantForm.bond_type);
      await axios.post(`${API_URL}/api/rewards`, {
        employee_id: grantForm.employee_id, // backend accepts either users._id or employees.id
        amount: bond ? bond.amount : 0,     // will be snapped to catalogue on server anyway
        bond_type: grantForm.bond_type,
        tenure_months: parseInt(grantForm.tenure_months, 10),
        description: grantForm.description.trim(),
      }, { withCredentials: true });
      toast.success(`${bond?.label || 'Bond'} granted to ${emp?.name || 'employee'}`);
      setGrantOpen(false);
      fetchMyRewards();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to grant reward');
    } finally { setGranting(false); }
  };

  const triggerUpload = (rewardId) => {
    setUploadTargetId(rewardId);
    certRef.current?.click();
  };

  const uploadCertificate = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTargetId) return;
    setUploadingId(uploadTargetId);
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${API_URL}/api/rewards/${uploadTargetId}/certificate`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Certificate uploaded');
      fetchMyRewards();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingId(null);
      setUploadTargetId(null);
      if (certRef.current) certRef.current.value = '';
    }
  };

  const downloadCertificate = async (rewardId, filename) => {
    try {
      const res = await axios.get(`${API_URL}/api/rewards/${rewardId}/certificate`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `reward-${rewardId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Download failed');
    }
  };

  const cancelReward = async (rewardId, employeeName) => {
    if (!window.confirm(`Cancel reward for ${employeeName}? This cannot be undone (only unredeemed rewards can be cancelled).`)) return;
    try {
      await axios.delete(`${API_URL}/api/rewards/${rewardId}`, { withCredentials: true });
      toast.success('Reward cancelled');
      fetchMyRewards();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cancel failed');
    }
  };

  const fetchBenefits = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/benefits`, { withCredentials: true });
      setBenefits(response.data);
    } catch (error) {
      console.error('Fetch benefits error:', error);
      toast.error('Failed to load benefits');
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    const icons = { esop: DollarSign, wfa_stipend: Briefcase, health_insurance: Heart };
    const Icon = icons[type] || Gift;
    return <Icon className="w-5 h-5" />;
  };

  const getTypeBadge = (type) => {
    const labels = { esop: 'ESOP', wfa_stipend: 'WFA Stipend', health_insurance: 'Health Insurance' };
    const classes = { esop: 'badge-success', wfa_stipend: 'badge-ai', health_insurance: 'badge-warning' };
    return <Badge className={classes[type]}>{labels[type] || type}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Default benefits info
  const defaultBenefits = [
    { type: 'esop', title: 'Employee Stock Option Plan', desc: 'Equity participation for eligible employees', icon: DollarSign },
    { type: 'wfa_stipend', title: 'Work From Anywhere Stipend', desc: 'Monthly allowance for remote work setup', icon: Briefcase },
    { type: 'health_insurance', title: 'Health Insurance', desc: 'Comprehensive medical coverage for you and family', icon: Heart },
    { type: 'learning', title: 'Learning Budget', desc: 'Annual budget for courses and certifications', icon: Gift },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="benefits-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Gift className="w-7 h-7 text-primary" />
          Benefits & Perks
        </h1>
        <p className="text-muted-foreground">View your flexible benefits and ESOP details</p>
      </div>

      {/* Available Benefits */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {defaultBenefits.map((benefit, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <benefit.icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{benefit.desc}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* My Benefits */}
      <Card>
        <CardHeader>
          <CardTitle>My Active Benefits</CardTitle>
        </CardHeader>
        <CardContent>
          {benefits.length > 0 ? (
            <div className="space-y-4">
              {benefits.map((benefit) => (
                <div key={benefit.id} className="flex items-start justify-between p-4 bg-muted rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-background rounded-md">
                      {getTypeIcon(benefit.type)}
                    </div>
                    <div>
                      {getTypeBadge(benefit.type)}
                      <div className="mt-2 text-sm">
                        {benefit.details && Object.entries(benefit.details).map(([key, value]) => (
                          <p key={key} className="text-muted-foreground">
                            <span className="capitalize">{key.replace('_', ' ')}</span>: <span className="text-foreground font-medium">{String(value)}</span>
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Since {new Date(benefit.effective_from).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              No active benefits assigned. Contact HR for enrollment.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Employee Appreciation Bond */}
      <input ref={certRef} type="file" className="hidden" accept="application/pdf,image/*" onChange={uploadCertificate} data-testid="cert-file-input" />
      <Card className="border-amber-300 dark:border-amber-800" data-testid="rewards-card">
        <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" /> Employee Appreciation Bond
            </CardTitle>
            <CardDescription>
              {isHR
                ? 'Recognise exceptional contributions with a fixed-value bond. Auto-credited on tenure completion; paid in F&F if the employee exits earlier.'
                : 'Appreciation bonds awarded to you. Amounts are payable after the mentioned tenure.'}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={rewardYear} onValueChange={setRewardYear}>
              <SelectTrigger className="h-8 w-28 text-xs" data-testid="reward-year-select"><SelectValue placeholder="Year" /></SelectTrigger>
              <SelectContent>
                {[String(new Date().getFullYear()), ...rewardYears.map(String)].filter((y, i, a) => a.indexOf(y) === i).map(y => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isHR && (
              <Button size="sm" onClick={openGrant} className="gap-1 bg-amber-500 hover:bg-amber-600 text-white" data-testid="grant-reward-btn">
                <Plus className="w-3.5 h-3.5" /> Award bond
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {rewardsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : rewards.length === 0 ? (
            <p className="text-center text-muted-foreground py-6 text-sm" data-testid="rewards-empty">
              {isHR ? 'No appreciation bonds granted in this year.' : 'No appreciation bonds yet — keep up the great work!'}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {rewards.map(r => {
                const isRedeemable = r.redeemable_now || (r.status === 'active' && r.redeem_after_date <= new Date().toISOString().slice(0, 10));
                const isLocked = r.status === 'active' && !isRedeemable;
                return (
                  <div key={r.id} className="rounded-lg border p-3 bg-gradient-to-br from-amber-50 to-white dark:from-amber-950/20 dark:to-transparent" data-testid={`reward-card-${r.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Award className="w-5 h-5 text-amber-600 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-lg leading-tight">₹{Number(r.amount).toLocaleString('en-IN')}</p>
                          {r.bond_label && (
                            <p className="text-xs font-medium text-amber-700 dark:text-amber-400" data-testid={`bond-label-${r.id}`}>{r.bond_label}</p>
                          )}
                          {isHR && <p className="text-xs text-muted-foreground">{r.employee_name || r.employee_email}</p>}
                          <p className="text-xs text-muted-foreground">Granted {r.granted_date} · {r.tenure_months} mo tenure</p>
                        </div>
                      </div>
                      {r.status === 'redeemed' && <Badge className="bg-emerald-600" data-testid={`status-redeemed-${r.id}`}><CheckCircle className="w-3 h-3 mr-1" /> Redeemed</Badge>}
                      {r.status === 'paid_in_fnf' && <Badge className="bg-blue-600">Paid in F&amp;F</Badge>}
                      {r.status === 'cancelled' && <Badge className="bg-gray-500"><XCircle className="w-3 h-3 mr-1" /> Cancelled</Badge>}
                      {isLocked && <Badge variant="outline" className="border-amber-400 text-amber-700 dark:text-amber-400" data-testid={`status-locked-${r.id}`}><Lock className="w-3 h-3 mr-1" /> Locked</Badge>}
                      {isRedeemable && r.status === 'active' && <Badge className="bg-amber-500">Redeemable</Badge>}
                    </div>
                    <p className="text-sm mt-2">{r.description}</p>
                    {isLocked && <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">Redeemable on <b>{r.redeem_after_date}</b></p>}
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {r.certificate_url && (
                        <Button size="sm" variant="outline" onClick={() => downloadCertificate(r.id, r.certificate_filename)} className="h-7 gap-1 text-xs" data-testid={`download-cert-${r.id}`}>
                          <Download className="w-3 h-3" /> Certificate
                        </Button>
                      )}
                      {isHR && !r.certificate_url && (
                        <Button size="sm" variant="outline" onClick={() => triggerUpload(r.id)} disabled={uploadingId === r.id} className="h-7 gap-1 text-xs" data-testid={`upload-cert-${r.id}`}>
                          {uploadingId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Upload certificate
                        </Button>
                      )}
                      {isHR && r.status === 'active' && (
                        <Button size="sm" variant="ghost" onClick={() => cancelReward(r.id, r.employee_name)} className="h-7 gap-1 text-xs text-rose-600" data-testid={`cancel-reward-${r.id}`}>
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Award Appreciation Bond dialog */}
      <Dialog open={grantOpen} onOpenChange={setGrantOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /> Award Employee Appreciation Bond</DialogTitle>
            <DialogDescription>Pick a bond type (amount is fixed). Employee sees it immediately (locked) and receives it auto-credited on the tenure date, or in F&amp;F if they leave earlier.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Employee</Label>
              <Select value={grantForm.employee_id} onValueChange={v => setGrantForm(f => ({ ...f, employee_id: v }))}>
                <SelectTrigger data-testid="grant-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => (
                    <SelectItem key={e.user_id || e.id} value={e.user_id || e.id}>{e.name} — {e.employee_id || 'no code'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Bond Type</Label>
                <Select value={grantForm.bond_type} onValueChange={v => setGrantForm(f => ({ ...f, bond_type: v }))}>
                  <SelectTrigger data-testid="grant-bond-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BOND_CATALOGUE.map(b => (
                      <SelectItem key={b.key} value={b.key}>{b.label} — ₹{b.amount.toLocaleString('en-IN')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground mt-1" data-testid="grant-bond-amount">Locked amount: ₹{selectedBond.amount.toLocaleString('en-IN')}</p>
              </div>
              <div>
                <Label className="text-xs">Tenure</Label>
                <Select value={grantForm.tenure_months} onValueChange={v => setGrantForm(f => ({ ...f, tenure_months: v }))}>
                  <SelectTrigger data-testid="grant-tenure-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">6 months</SelectItem>
                    <SelectItem value="8">8 months</SelectItem>
                    <SelectItem value="12">12 months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Citation / Reason (shown on payslip when paid)</Label>
              <Textarea rows={3} value={grantForm.description} onChange={e => setGrantForm(f => ({ ...f, description: e.target.value }))} data-testid="grant-desc-input" placeholder="Star performer H2-2025 · Launched wisewit.ai/insights v2 ahead of schedule" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantOpen(false)}>Cancel</Button>
            <Button onClick={submitGrant} disabled={granting} className="gap-1" data-testid="grant-submit-btn">
              {granting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />} Award
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ESOP Info */}
      <Card className="border-[hsl(var(--success))]/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-[hsl(var(--success))]" />
            ESOP Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-muted-foreground">Vesting Schedule</p>
              <p className="font-semibold">4 Years with 1 Year Cliff</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Monthly Vesting</p>
              <p className="font-semibold">After cliff period</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Exercise Window</p>
              <p className="font-semibold">90 days post exit</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            Contact HR for your individual ESOP grant details and vesting status.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default BenefitsPage;
